import { useState, useEffect, useRef, useContext } from 'react'
import Image from 'next/image'
import { contentfulLoader } from '../../core/Image/contentfulLoader'
import { SiteBuilderContext } from '../../../renderer/context/SiteBuilderContext'
import { Typography, ButtonLink, Spacer, Box } from '@telus-uds/components-web'
import {
  Container,
  Tile,
  CloudTextArea,
  TileContainer,
  TextSpacer,
  VisibleCloudText,
  CenterCloudDiv,
  CenterButton,
  BlackGradient,
} from './Styles'
import {
  getBlurRange,
  durstenfeldArrayShuffle,
  getNumberOfTilesPerHalfHorizontally,
  getRandomNumber,
  getRandomConstrainedYCoord,
  getRandomConstrainedXCoord,
  getTilePosition,
  getRowPosition,
  getFilledGridLocation,
  getXPositionTileArray,
  getContainerHeight,
  fillImageArrayWithRepeatImages,
  getTileRanges,
  GridLocation,
  TilePositions,
} from './utils/AnimatedCloudTile.util'
import { gsap } from 'gsap/dist/gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

type AnimatedCloudTileProps = {
  scrollEnd: string
  mobileBP: number
  setTileHeight: (value: number) => void
  staticRandomizedPos: boolean
  tileName: string
  scrollTriggerID: string
  startingHeader: string
  endingHeader: string
  startingSmallText: string
  endingSmallText: string
  buttonLink: string
  buttonText: string
  cloudTiles: string[]
  mobileCloudTiles: string[]
  tileDensityFactor: number
  pixelBlurAmount: number
  lowerBoundScaleRange: number
  upperBoundScaleRange: number
  blurPercent: number
  animationScrub: number
  showHalfAnim: boolean
}

const AnimatedCloudTile = ({
  scrollEnd,
  mobileBP,
  setTileHeight,
  staticRandomizedPos,
  tileName,
  scrollTriggerID,
  startingHeader,
  endingHeader,
  startingSmallText,
  endingSmallText,
  buttonLink,
  buttonText,
  cloudTiles,
  mobileCloudTiles,
  tileDensityFactor,
  pixelBlurAmount,
  lowerBoundScaleRange,
  upperBoundScaleRange,
  blurPercent,
  animationScrub,
  showHalfAnim,
}: AnimatedCloudTileProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const tileRanges = useRef<number[][]>([])
  const tileSpacing = useRef<number>(
    typeof window !== 'undefined' && window.document.body.clientWidth < mobileBP ? 12 : 25
  )
  const maxTilesPerRow = 7
  const maxTilesPerRowMobile = 3
  const gridLocation = useRef<GridLocation[]>([])
  const cloudTextAreaRef = useRef<HTMLDivElement>(null)
  const tileContainerRef = useRef<HTMLDivElement>(null)
  const startingVisibleCloudTextRef = useRef<HTMLDivElement>(null)
  const endingVisibleCloudTextRef = useRef<HTMLDivElement>(null)
  const containerHeight = useRef(0)
  const topBottomTileCounterX = useRef<number>(0)
  const topBottomTileCounterY = useRef<number>(0)
  const isDesktop = useRef<boolean>(null)
  const centerTextPadding = 100
  const mobilePadding = 30
  const resizeTileAddTracker = useRef<number>(0)
  const initialPageLoad = useRef<boolean>(true)
  const [forceRerender, setForceRerender] = useState(0)
  const [prevWidth, setPrevWidth] = useState(0)

  const xPositionTile = useRef<TilePositions>({
    even: [],
    odd: [],
  })

  const { isWebpSupported } = useContext(SiteBuilderContext)
  const webpLoader = contentfulLoader(isWebpSupported)
  const aspectRatio = 0.5625 // 16:9
  const tileWidth = 220
  const tileHeight = tileWidth * aspectRatio
  const tileDimensions = useRef({
    width: tileWidth,
    height: tileHeight,
  })
  const [isMobile, setIsMobile] = useState(false)
  const [containerOverflow, setContainerOverflow] = useState<string>(null)
  const [tileContainerTop, setTileContainerTop] = useState(0)

  gsap.registerPlugin(ScrollTrigger)

  const setProperties = (screenWidth: number, reducedMotionOn: boolean) => {
    const mobile = screenWidth < mobileBP
    resetRefs()
    setIsMobile(mobile)
    if (!mobile) {
      ;[resizeTileAddTracker.current, cloudTiles] = fillImageArrayWithRepeatImages(
        tileDimensions.current.height,
        tileSpacing.current,
        maxTilesPerRow,
        cloudTiles,
        resizeTileAddTracker.current
      )
    }

    setTileDimensions(screenWidth, mobile ? maxTilesPerRowMobile - 1 : maxTilesPerRow - 1)
    gridLocation.current = getFilledGridLocation(
      mobile ? maxTilesPerRowMobile - 1 : maxTilesPerRow - 1,
      mobile ? mobileCloudTiles.length : cloudTiles.length
    )
    xPositionTile.current = getXPositionTileArray(
      mobile ? maxTilesPerRowMobile : maxTilesPerRow,
      screenWidth,
      tileDimensions.current.width,
      tileSpacing.current
    )

    containerHeight.current = showHalfAnim
      ? window.innerHeight + tileDimensions.current.height
      : getContainerHeight(
          gridLocation.current,
          containerHeight.current,
          tileSpacing.current,
          tileDimensions.current,
          aspectRatio
        )

    tileRanges.current = getTileRanges(
      mobile ? mobileCloudTiles.length : cloudTiles.length,
      gridLocation.current,
      xPositionTile.current,
      containerRef.current.clientWidth,
      tileDimensions.current,
      containerHeight.current,
      tileSpacing.current
    )

    if ((staticRandomizedPos && reducedMotionOn) || !reducedMotionOn) {
      setAnimations(mobile ? mobileCloudTiles : cloudTiles, reducedMotionOn)
      ScrollTrigger.refresh()
    }

    if (reducedMotionOn && staticRandomizedPos) {
      setContainerOverflow('hidden')
    } else {
      setContainerOverflow('visible')
    }

    if (reducedMotionOn) {
      setTileContainerTop(-tileDimensions.current.height / 2)
    }
  }

  useEffect(() => {
    setTileHeight(tileHeight)
    ScrollTrigger.config({
      autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load',
      ignoreMobileResize: true,
    })
    gsap.config({
      nullTargetWarn: false,
    })
  }, [])

  useEffect(() => {
    const reducedMotionOn = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setProperties(containerRef.current.clientWidth, reducedMotionOn)
    setForceRerender(forceRerender + containerRef.current.clientWidth + 1)

    if (window.document.body.clientWidth >= mobileBP) {
      isDesktop.current = true
    } else {
      isDesktop.current = false
    }
    if (initialPageLoad.current) {
      initialPageLoad.current = false
      gsap.to(tileContainerRef.current, { y: -tileDimensions.current.height / 2, delay: 3 })
    }

    const setPropertiesAndRerender = () => {
      tileSpacing.current = window.document.body.clientWidth < mobileBP ? 12 : 25
      setProperties(containerRef.current.clientWidth, reducedMotionOn)
      setForceRerender(forceRerender + containerRef.current.clientWidth + 3)
    }

    const endOfResizeAction = () => {
      setPropertiesAndRerender()
      setPropertiesAndRerender() // required to ensure dynamicically added tiles render

      if (!reducedMotionOn) {
        gsap.to(tileContainerRef.current, { y: -(tileDimensions.current.height / 2) })
      }
    }

    // limit function call to only the end of a resize
    let action: NodeJS.Timeout
    const windowResized = () => {
      if (window.innerWidth !== prevWidth) {
        setPrevWidth(window.innerWidth)
        clearTimeout(action)
        action = setTimeout(endOfResizeAction, 1)
      }
    }
    // call function on initial load to ensure correct tile count on page load
    windowResized()
    window.addEventListener('resize', windowResized)
    return () => window.removeEventListener('resize', windowResized)
  }, [])

  // recalculates but does not rerender anything when dynamic image tiles load
  // this ensures that all tiles have animations
  useEffect(() => {
    const reducedMotionOn = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (ScrollTrigger.getById(scrollTriggerID)) {
      ScrollTrigger.getById(scrollTriggerID).kill(true)
    }
    setProperties(containerRef.current.clientWidth, reducedMotionOn)
    ScrollTrigger.refresh()
  }, [tileDimensions.current.width, forceRerender])

  // recalculates and rerenders after tileDimension width adjusts
  useEffect(() => {
    const reducedMotionOn = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setProperties(containerRef.current.clientWidth, reducedMotionOn)
  }, [tileDimensions.current.width])

  useEffect(() => {
    ScrollTrigger.refresh()
  }, [containerRef.current])

  const setAnimations = (images: string[], reducedMotionOn: boolean) => {
    const tl = gsap.timeline()
    const blurRange = getBlurRange(lowerBoundScaleRange, upperBoundScaleRange, blurPercent)
    const isMobile = window.document.body.clientWidth < mobileBP

    // get index and number of tiles that show up in viewport above and below center text
    const numHorizontalTiles = getNumberOfTilesPerHalfHorizontally(
      tileDensityFactor,
      tileWidth,
      upperBoundScaleRange,
      cloudTextAreaRef.current.clientWidth
    )
    const horizontalInterval = window.document.body.clientWidth / numHorizontalTiles
    const shuffledSlicedTileIndices = durstenfeldArrayShuffle([...Array(images.length).keys()])
    const topBottomTileIndices = shuffledSlicedTileIndices.slice(0, numHorizontalTiles * 2)
    let topBottomCounter = 0
    let alternateNewTilePosTrackerBottom = false
    let alternateNewTilePosTrackerTop = true

    // get index and number of tiles that show up in viewport left and right of center text
    const numHorizontalTilesSides = getNumberOfTilesPerHalfHorizontally(
      tileDensityFactor,
      tileWidth,
      upperBoundScaleRange,
      cloudTextAreaRef.current.clientWidth,
      true
    )
    const horizontalIntervalSides =
      (window.document.body.clientWidth - cloudTextAreaRef.current.clientWidth - tileWidth) /
      (2 * numHorizontalTilesSides)
    const leftSideTilesIndices = shuffledSlicedTileIndices.slice(
      numHorizontalTiles * 2,
      numHorizontalTiles * 2 + numHorizontalTilesSides - 1
    )
    const rightSideTilesIndices = shuffledSlicedTileIndices.slice(
      numHorizontalTiles * 2 + numHorizontalTilesSides,
      2 * (numHorizontalTiles + numHorizontalTilesSides)
    )
    let leftCounter = 0
    let rightCounter = 0
    images.forEach((_, idx) => {
      const sideTilesYUpperRange =
        tileRanges.current[idx][2] +
        window.innerHeight / 2 +
        cloudTextAreaRef.current.clientHeight +
        tileDimensions.current.height / 2
      const sideTilesYLowerRange =
        tileRanges.current[idx][2] +
        window.innerHeight / 2 -
        cloudTextAreaRef.current.clientHeight -
        tileDimensions.current.height / 2
      let randomScaleValue = getRandomNumber(lowerBoundScaleRange, upperBoundScaleRange)
      let randomXCoord: number, randomYCoord: number

      if (idx == shuffledSlicedTileIndices[shuffledSlicedTileIndices.length - 1]) {
        // position large image on bottom left of screen
        randomScaleValue = isMobile ? upperBoundScaleRange : 3
        randomXCoord = tileRanges.current[idx][0] + (randomScaleValue * tileWidth) / 4
        randomYCoord = tileRanges.current[idx][3] - (randomScaleValue * tileDimensions.current.height) / 4

        if (isMobile) {
          randomYCoord += tileDimensions.current.height / 2
        }
      } else if (idx == shuffledSlicedTileIndices[shuffledSlicedTileIndices.length - 2] && !isMobile) {
        // position control image to the left of center text
        randomScaleValue = 1
        randomXCoord =
          tileRanges.current[idx][0] +
          window.document.body.clientWidth / 2 -
          cloudTextAreaRef.current.clientWidth / 2 -
          tileWidth / 2 -
          centerTextPadding
        randomYCoord = (sideTilesYUpperRange + sideTilesYLowerRange) / 2
      } else if (topBottomTileIndices.includes(idx)) {
        // position images on the top and bottom of text
        randomXCoord = getRandomConstrainedXCoord(
          horizontalInterval,
          randomScaleValue,
          tileRanges.current[idx][0],
          topBottomCounter < topBottomTileIndices.length / 2,
          topBottomCounter,
          tileWidth,
          cloudTextAreaRef.current.clientWidth,
          tileDimensions.current.width
        )
        randomYCoord = getRandomConstrainedYCoord(
          tileRanges.current[idx][2],
          tileRanges.current[idx][3],
          randomScaleValue,
          topBottomCounter < topBottomTileIndices.length / 2,
          cloudTextAreaRef.current.clientHeight,
          tileDimensions.current.height,
          mobilePadding,
          alternateNewTilePosTrackerBottom,
          alternateNewTilePosTrackerTop,
          isMobile
        )

        if (topBottomCounter < topBottomTileIndices.length / 2) {
          alternateNewTilePosTrackerTop = !alternateNewTilePosTrackerTop
        } else {
          alternateNewTilePosTrackerBottom = !alternateNewTilePosTrackerBottom
        }

        topBottomCounter += 1
      } else if (leftSideTilesIndices.includes(idx) && !isMobile) {
        // position images to the left of center text
        randomXCoord = getRandomConstrainedXCoord(
          horizontalIntervalSides,
          randomScaleValue,
          tileRanges.current[idx][0],
          topBottomCounter < topBottomTileIndices.length / 2,
          leftCounter,
          tileWidth,
          cloudTextAreaRef.current.clientWidth,
          tileDimensions.current.width,
          'left'
        )
        randomScaleValue = getRandomNumber(0.8, 1)
        randomYCoord = getRandomNumber(sideTilesYLowerRange, sideTilesYUpperRange)
        leftCounter += 1
      } else if (rightSideTilesIndices.includes(idx) && !isMobile) {
        // position images to the right of center text
        randomXCoord = getRandomConstrainedXCoord(
          horizontalIntervalSides,
          randomScaleValue,
          tileRanges.current[idx][0],
          topBottomCounter < topBottomTileIndices.length / 2,
          rightCounter,
          tileWidth,
          cloudTextAreaRef.current.clientWidth,
          tileDimensions.current.width,
          'right'
        )

        randomYCoord = getRandomNumber(sideTilesYLowerRange, sideTilesYUpperRange)
        rightCounter += 1
      } else {
        // all tiles not referenced by index moves offscreen randomized in each direction
        const moveDirectionChooser = Math.floor(Math.random() * 4)
        const moveSecondaryDirection = Math.floor(Math.random() * 2)
        switch (moveDirectionChooser) {
          case 0:
            randomXCoord = -window.document.body.clientWidth
            randomYCoord = Math.floor(Math.random() * 1000)
          case 1:
            randomXCoord = window.document.body.clientWidth
            randomYCoord = Math.floor(Math.random() * 1000)
          case 2:
            randomYCoord = containerHeight.current + 1000
            randomXCoord = Math.floor(Math.random() * 1000)
          default:
            randomYCoord = -containerHeight.current - 1000
            randomXCoord = Math.floor(Math.random() * 1000)
        }

        if (moveDirectionChooser < 2 && moveSecondaryDirection) {
          randomYCoord *= -1
        } else if (moveDirectionChooser >= 2 && moveSecondaryDirection) {
          randomXCoord *= -1
        }
      }

      const cloudTileParams = {
        x: randomXCoord,
        y: randomYCoord,
        duration: reducedMotionOn ? 0 : 1200,
        scale: randomScaleValue,
        filter:
          randomScaleValue > blurRange[0] && randomScaleValue < blurRange[1]
            ? 'blur(0px)'
            : `blur(${pixelBlurAmount}px)`,
      }

      tl.set(
        `#${tileName}${idx}`,
        {
          css:
            idx == shuffledSlicedTileIndices[shuffledSlicedTileIndices.length - 1]
              ? { css: { zIndex: 6 } }
              : randomScaleValue >= blurRange[1]
              ? { css: { zIndex: 5 } }
              : randomScaleValue <= blurRange[0]
              ? { css: { zIndex: 0 } }
              : { css: { zIndex: 3 } },
        },
        0
      )
        .to(`#${tileName}${idx}`, cloudTileParams, 0)
        .to(`#${tileName}${idx}`, cloudTileParams, '>')
        .addLabel('gradientFadeInMarker', '>')

      if (!reducedMotionOn && !showHalfAnim) {
        tl.to(
          `#${tileName}${idx}`,
          {
            x: 0,
            y: 0,
            duration: 1200,
            filter: 'blur(0px)',
            scale: 1,
          },
          '>'
        )
      }
    })

    tl.to(startingVisibleCloudTextRef.current, { opacity: 0, duration: reducedMotionOn ? 0 : 200 }, 0).to(
      endingVisibleCloudTextRef.current,
      { opacity: 1, duration: reducedMotionOn ? 0 : 200 },
      '>'
    )

    if (!reducedMotionOn) {
      ScrollTrigger.normalizeScroll(true)
      ScrollTrigger.create({
        id: scrollTriggerID,
        trigger: containerRef.current,
        animation: tl,
        start: `center center+=${tileDimensions.current.height / 2}`,
        end: `top ${scrollEnd}`,
        scrub: animationScrub,
        refreshPriority: 1,
        pin: true,
        invalidateOnRefresh: true,
      })
    }
  }

  const resetRefs = () => {
    if (ScrollTrigger.getById(scrollTriggerID)) {
      ScrollTrigger.getById(scrollTriggerID).kill(true)
    }
    topBottomTileCounterX.current = 0
    topBottomTileCounterY.current = 0
    gridLocation.current = []
    xPositionTile.current = {
      even: [],
      odd: [],
    }
    tileRanges.current = []
  }

  const setTileDimensions = (screenWidth: number, d: number) => {
    tileDimensions.current.width = (screenWidth - tileSpacing.current * (d - 1)) / d
    tileDimensions.current.height = tileDimensions.current.width * aspectRatio
  }

  const renderCloudText = (header, smallText) => {
    return (
      <CenterCloudDiv>
        <div ref={cloudTextAreaRef}>
          <Box vertical={4} horizontal={4}>
            <Typography variant={{ size: 'display2', inverse: true }} heading="h2">
              {header}
            </Typography>
            <Spacer space={2} />
            <Typography variant={{ size: 'medium', inverse: true }}>{smallText}</Typography>
            <Spacer space={4} />
            <CenterButton>
              <ButtonLink href={buttonLink}>{buttonText}</ButtonLink>
            </CenterButton>
          </Box>
        </div>
      </CenterCloudDiv>
    )
  }

  return (
    <Container
      data-testid={'animated-cloud-tile-testid'}
      overflow={containerOverflow}
      height={containerHeight.current}
      ref={containerRef}
      forceRerender={forceRerender}
    >
      <TileContainer top={tileContainerTop} ref={tileContainerRef} paddingBottom={tileDimensions.current.height / 2}>
        <CloudTextArea>
          <TextSpacer>{renderCloudText(startingHeader, startingSmallText)}</TextSpacer>
          <VisibleCloudText opacity={1} ref={startingVisibleCloudTextRef}>
            {renderCloudText(startingHeader, startingSmallText)}
          </VisibleCloudText>
          <VisibleCloudText opacity={0} ref={endingVisibleCloudTextRef}>
            {renderCloudText(endingHeader, endingSmallText)}
          </VisibleCloudText>
        </CloudTextArea>
        <BlackGradient height={containerHeight.current + tileDimensions.current.height / 2} />
        {isMobile &&
          mobileCloudTiles.length &&
          mobileCloudTiles.map((image, idx) => {
            return (
              <Tile
                id={`${tileName}${idx}`}
                left={getTilePosition(idx, gridLocation.current, xPositionTile.current)}
                top={getRowPosition(idx, gridLocation.current, tileDimensions.current.height, tileSpacing.current)}
                width={tileDimensions.current.width}
                height={tileDimensions.current.height}
                key={idx}
              >
                {image && <Image loader={webpLoader} src={image} alt="" layout="fill" objectFit="fill" />}
              </Tile>
            )
          })}
        {!isMobile &&
          cloudTiles &&
          cloudTiles.map((image, idx) => {
            return (
              <Tile
                id={`${tileName}${idx}`}
                left={getTilePosition(idx, gridLocation.current, xPositionTile.current)}
                top={getRowPosition(idx, gridLocation.current, tileDimensions.current.height, tileSpacing.current)}
                width={tileDimensions.current.width}
                height={tileDimensions.current.height}
                key={idx}
              >
                {image && <Image loader={webpLoader} src={image} alt="" layout="fill" objectFit="fill" />}
              </Tile>
            )
          })}
      </TileContainer>
    </Container>
  )
}

export default AnimatedCloudTile
